import { CommunicationStatus } from '../../../common/communications/CommunicationStatusChip';

export interface InboxReply {
  inboundSmsId: string;
  replyId: string;
  inReplyToMessageId: string | null;
  userId: string | null;
  contactNumber: string;
  body: string;
  receivedAt: string;
}

export interface ConversationMessage {
  direction: 'outbound' | 'inbound';
  id: string;
  body: string;
  at: string;
  sentBy?: string | null;
  deliveryStatus?: string | null;
}

export interface GetConversationRequest {
  userId?: string;
  contactNumber?: string;
}

export interface GetConversationResponse {
  messages: ConversationMessage[];
}

export interface SendSmsRequest {
  profileIds: string[];
  sentBy: string;
  message: string;
  senderName?: string | null;
  allowReplies?: boolean;
}

export interface PaginationMeta {
  offset: number;
  limit: number;
  total: number;
}

export interface GetInboxRequest {
  search?: string;
  receivedFrom?: string;
  receivedTo?: string;
  offset?: number;
  limit?: number;
}

export interface GetInboxResponse {
  inboundSms: InboxReply[];
  pagination: PaginationMeta;
}

export interface LookupContactNumberRequest {
  contactNumbers: string[];
}

export interface CommunicationsPerson {
  volunteerId: string | null;
  profileId: string;
  userId: string | null;
  firstName: string;
  preferredName: string | null;
  lastName: string;
  avatarUrl: string | null;
}

export interface LookupContactNumberResponse {
  volunteersByNumber: Record<string, CommunicationsPerson[]>;
}

export interface LookupProfilesRequest {
  profileIds?: string[];
  userIds?: string[];
}

export interface LookupProfilesResponse {
  profilesById: Record<string, CommunicationsPerson>;
  profilesByUserId: Record<string, CommunicationsPerson>;
}

export const AVATAR_SIZE = 35;

export const personName = (person: CommunicationsPerson): string =>
  `${person.preferredName || person.firstName} ${person.lastName}`;

export const personInitials = (person: CommunicationsPerson): string =>
  `${(person.preferredName || person.firstName).charAt(0)}${person.lastName.charAt(0)}`;

export interface SentCommunication {
  channel: 'sms' | 'email';
  groupKey: string;
  recipientCount: number;
  sentAt: string | null;
  sentBy: string | null;
  tag: string | null;
  preview: string;
  repliable: boolean;
  recipient: string | null;
  recipientUserIds: string[];
  deliveredCount: number;
  failedCount: number;
  repliedCount: number;
  openedCount: number;
  clickedCount: number;
}

export interface CommunicationStatusSummary {
  status: CommunicationStatus;
  count?: number;
}

const FAILED_ROSTER_STATUSES = ['failed', 'rejected', 'expired'];

export const rosterStatus = (roster: RosterSms): CommunicationStatus => {
  if (roster.direction === 'received') return 'replied';
  if (roster.status === 'delivered') return 'delivered';
  if (FAILED_ROSTER_STATUSES.includes(roster.status ?? '')) return 'failed';
  return 'sent';
};

/**
 * One chip for a single recipient, using the same precedence as the profile's Communications tab.
 * A batch gets a counted chip per outcome instead.
 */
export const sentStatuses = (sent: SentCommunication): CommunicationStatusSummary[] => {
  const failed: CommunicationStatus = sent.channel === 'email' ? 'bounced' : 'failed';

  if (sent.recipientCount === 1) {
    if (sent.failedCount > 0) return [{ status: failed }];
    if (sent.repliedCount > 0) return [{ status: 'replied' }];
    if (sent.clickedCount > 0) return [{ status: 'clicked' }];
    if (sent.openedCount > 0) return [{ status: 'opened' }];
    if (sent.deliveredCount > 0) return [{ status: 'delivered' }];
    return [{ status: 'sent' }];
  }

  const counted: CommunicationStatusSummary[] = [
    { status: 'delivered', count: sent.deliveredCount },
    { status: 'opened', count: sent.openedCount },
    { status: 'clicked', count: sent.clickedCount },
    { status: 'replied', count: sent.repliedCount },
    { status: failed, count: sent.failedCount },
  ];
  const outcomes = counted.filter((summary) => (summary.count ?? 0) > 0);

  return outcomes.length > 0 ? outcomes : [{ status: 'sent', count: sent.recipientCount }];
};

export interface GetSentRequest {
  channel?: 'sms' | 'email';
  search?: string;
  tag?: string;
  sentFrom?: string;
  sentTo?: string;
  automated?: boolean;
  offset?: number;
  limit?: number;
}

export interface GetSentResponse {
  sent: SentCommunication[];
  pagination: PaginationMeta;
}

export interface RosterSms {
  smsLogId: string;
  messageId: string;
  direction: 'sent' | 'received';
  sentAt: string;
  preview: string;
  status: string | null;
  publishedRosteringId: string;
  volunteerId: string;
  volunteerName: string;
  volunteerProfileId: string;
  volunteerUserId: string | null;
  volunteerAvatarUrl: string | null;
  activityId: string;
  activityName: string;
  activityDate: string;
}

export interface GetRosterSmsRequest {
  direction?: 'sent' | 'received';
  search?: string;
  sentFrom?: string;
  sentTo?: string;
  offset?: number;
  limit?: number;
}

export interface GetRosterSmsResponse {
  rosterSms: RosterSms[];
  pagination: PaginationMeta;
}

export type CommunicationLogItem =
  | { kind: 'sent'; sortAt: string; sent: SentCommunication }
  | { kind: 'roster'; sortAt: string; roster: RosterSms };

export const mergeCommunicationLog = (sent: SentCommunication[], rosterSms: RosterSms[]): CommunicationLogItem[] => {
  const items: CommunicationLogItem[] = [
    ...sent
      .filter((item) => item.sentAt !== null)
      .map((item) => ({ kind: 'sent', sortAt: item.sentAt as string, sent: item } as CommunicationLogItem)),
    ...rosterSms.map((item) => ({ kind: 'roster', sortAt: item.sentAt, roster: item } as CommunicationLogItem)),
  ];

  return items.sort((a, b) => b.sortAt.localeCompare(a.sortAt));
};

/**
 * Ad-hoc sends carry a tag so the log can filter on them, but the tag says nothing a reader needs,
 * so no chip is drawn. `volunteer-sms` is the value the send path wrote before 2026-09-23.
 */
const AD_HOC_TAGS = ['ad-hoc', 'volunteer-sms', 'volunteer-email'];

export const isAdHocTag = (tag: string | null): boolean => tag !== null && AD_HOC_TAGS.includes(tag);

export const humaniseTag = (tag: string | null): string | null => {
  if (!tag || isAdHocTag(tag)) return null;
  return tag
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const TYPE_OPTIONS = [
  { label: 'All types', value: 'all' },
  { label: 'Ad hoc', value: 'manual' },
  { label: 'Automated', value: 'automated' },
];

export const CHANNEL_OPTIONS = [
  { label: 'All channels', value: 'all' },
  { label: 'SMS only', value: 'sms' },
  { label: 'Email only', value: 'email' },
];
